import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { WorkspaceMemberInfo } from '../workspaces/workspacesApi';
import type { BoardColumn } from '../boardColumns/boardColumnsApi';
import type { Label } from '../labels/labelsApi';
import type { Sprint } from '../sprints/sprintsApi';
import { epicsApi, type Epic } from '../epics/epicsApi';
import { projectsApi, type Project } from '../projects/projectsApi';
import { type Task, type UpdateTaskRequest, tasksApi } from '../tasks/tasksApi';
import RichTextEditor, { RichTextContent } from './RichTextEditor';

type GetToken = () => string | null;
type DoRefresh = () => Promise<boolean>;

interface BaseProps {
  workspaceId: string;
  columns: BoardColumn[];
  labels: Label[];
  members: WorkspaceMemberInfo[];
  sprints?: Sprint[];
  getToken: GetToken;
  doRefresh: DoRefresh;
}

interface CreateProps extends BaseProps {
  mode: 'create';
  defaultProjectId?: string;
  defaultEpicId?: string;
  defaultSprintId?: string;
  currentUserId: string;
  onCreate: (task: Task) => void;
  onCancel?: () => void;
}

interface ViewProps extends BaseProps {
  mode: 'view';
  task: Task;
  onUpdate: (taskId: string, updates: UpdateTaskRequest) => Promise<Task>;
}

type TaskPanelProps = CreateProps | ViewProps;

export interface TaskPanelHandle {
  trySubmit: () => Promise<void>;
  startEdit: () => void;
}

function Row({ label, children, onDoubleClick }: { label: string; children: React.ReactNode; onDoubleClick?: () => void }) {
  return (
    <div
      onDoubleClick={onDoubleClick}
      title={onDoubleClick ? 'Double-click to edit' : undefined}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid var(--color-border)',
        cursor: onDoubleClick ? 'default' : undefined,
      }}
    >
      <span style={{ width: 88, fontSize: 12, color: 'var(--color-text-muted)', flexShrink: 0, paddingTop: 2 }}>{label}</span>
      <div style={{ flex: 1, fontSize: 13 }}>{children}</div>
    </div>
  );
}

const COMPLEXITY_LABELS: Record<number, string> = {
  0: '—', 1: '1 — Trivial', 2: '2 — Simple', 3: '3 — Medium', 4: '4 — Complex', 5: '5 — Unknown',
};

const TaskPanel = forwardRef<TaskPanelHandle, TaskPanelProps>(function TaskPanel(props, ref) {
  const { workspaceId, columns, labels, members, sprints, getToken, doRefresh } = props;

  // ── Create-mode state ──────────────────────────────────────────────────────
  const [createTitle, setCreateTitle] = useState('');
  const [createEpicId, setCreateEpicId] = useState(props.mode === 'create' ? (props.defaultEpicId ?? '') : '');
  const [createProjectId, setCreateProjectId] = useState(props.mode === 'create' ? (props.defaultProjectId ?? '') : '');
  const [createStatus, setCreateStatus] = useState(columns[0]?.columnId ?? '');
  const [createPoints, setCreatePoints] = useState(1);
  const [createComplexity, setCreateComplexity] = useState(0);
  const [createStartDate, setCreateStartDate] = useState('');
  const [createEndDate, setCreateEndDate] = useState('');
  const [createLabelIds, setCreateLabelIds] = useState<string[]>([]);
  const [createAssigneeIds, setCreateAssigneeIds] = useState<string[]>(
    props.mode === 'create' ? [props.currentUserId] : [],
  );
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // ── View/edit-mode state ───────────────────────────────────────────────────
  const task = props.mode === 'view' ? props.task : null;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [editTitle, setEditTitle] = useState(task?.title ?? '');
  const [editDescription, setEditDescription] = useState(task?.description ?? '');
  const [editStatus, setEditStatus] = useState(task?.status ?? '');
  const [editEpicId, setEditEpicId] = useState(task?.epicId ?? '');
  const [editProjectId, setEditProjectId] = useState(task?.projectId ?? '');
  const [editPoints, setEditPoints] = useState(task?.points ?? 1);
  const [editComplexity, setEditComplexity] = useState(task?.complexity ?? 0);
  const [editStartDate, setEditStartDate] = useState(task?.startDate ?? '');
  const [editEndDate, setEditEndDate] = useState(task?.endDate ?? '');
  const [editLabelIds, setEditLabelIds] = useState<string[]>(task?.labelIds ?? []);
  const [editAssigneeIds, setEditAssigneeIds] = useState<string[]>(task?.assigneeIds ?? []);
  const [editSprintId, setEditSprintId] = useState(task?.sprintId ?? '');

  useEffect(() => {
    if (!task) return;
    setEditTitle(task.title);
    setEditDescription(task.description);
    setEditStatus(task.status);
    setEditEpicId(task.epicId);
    setEditProjectId(task.projectId);
    setEditPoints(task.points);
    setEditComplexity(task.complexity ?? 0);
    setEditStartDate(task.startDate ?? '');
    setEditEndDate(task.endDate ?? '');
    setEditLabelIds(task.labelIds);
    setEditAssigneeIds(task.assigneeIds);
    setEditSprintId(task.sprintId ?? '');
    setEditing(false);
  }, [task?.taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Epics / projects ───────────────────────────────────────────────────────
  const [epics, setEpics] = useState<Epic[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const loadEpics = useCallback(() => {
    epicsApi.list(workspaceId, getToken, doRefresh)
      .then(data => setEpics(data.epics))
      .catch(() => {});
  }, [workspaceId, getToken, doRefresh]);

  useEffect(() => {
    if (props.mode === 'create') loadEpics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.mode === 'view' && editing) loadEpics();
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjects = useCallback((epicId: string) => {
    if (!epicId) { setProjects([]); return; }
    setLoadingProjects(true);
    projectsApi.list(workspaceId, epicId, getToken, doRefresh)
      .then(data => setProjects(data.projects))
      .catch(() => {})
      .finally(() => setLoadingProjects(false));
  }, [workspaceId, getToken, doRefresh]);

  useEffect(() => {
    if (props.mode === 'create' && createEpicId) loadProjects(createEpicId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.mode === 'view' && editing && editEpicId) loadProjects(editEpicId);
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click-outside to save (edit mode only) ────────────────────────────────
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!editing) return;
    function onMouseDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        handleSave();
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }); // intentionally re-runs each render so handleSave always reads latest state

  // ── Imperative handle ──────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    trySubmit: async () => {
      if (props.mode === 'create' && createTitle.trim() && createProjectId) {
        await doCreate();
      }
    },
    startEdit: () => {
      if (props.mode === 'view' && !editing) startEdit();
    },
  }));

  // ── Create ─────────────────────────────────────────────────────────────────
  async function doCreate() {
    if (!createTitle.trim() || !createProjectId) return;
    setCreating(true);
    setCreateError('');
    try {
      let created = await tasksApi.create(
        workspaceId,
        createProjectId,
        {
          title: createTitle.trim(),
          description: createDescription.trim(),
          points: createPoints,
          assigneeIds: createAssigneeIds,
          labelIds: createLabelIds,
          complexity: createComplexity > 0 ? createComplexity : undefined,
          startDate: createStartDate || undefined,
          endDate: createEndDate || undefined,
        },
        getToken,
        doRefresh,
      );
      if (createStatus && columns[0]?.columnId && createStatus !== columns[0].columnId) {
        created = await tasksApi.update(workspaceId, created.taskId, { status: createStatus }, getToken, doRefresh);
      }
      if (props.mode === 'create' && props.defaultSprintId) {
        created = await tasksApi.update(workspaceId, created.taskId, { sprintId: props.defaultSprintId }, getToken, doRefresh);
      }
      if (props.mode === 'create') props.onCreate(created);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create task');
      setCreating(false);
      throw err;
    }
    setCreating(false);
  }

  // ── Save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (props.mode !== 'view' || !task) return;
    setSaving(true);
    setSaveError('');
    try {
      await props.onUpdate(task.taskId, {
        title: editTitle.trim() || task.title,
        description: editDescription,
        status: editStatus,
        projectId: editProjectId,
        points: editPoints,
        complexity: editComplexity > 0 ? editComplexity : undefined,
        startDate: editStartDate || '',
        endDate: editEndDate || '',
        labelIds: editLabelIds,
        assigneeIds: editAssigneeIds,
        sprintId: editSprintId || 'NONE',
      });
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function startEdit() {
    if (editing) return;
    setEditTitle(task?.title ?? '');
    setEditDescription(task?.description ?? '');
    setEditStatus(task?.status ?? '');
    setEditEpicId(task?.epicId ?? '');
    setEditProjectId(task?.projectId ?? '');
    setEditPoints(task?.points ?? 1);
    setEditComplexity(task?.complexity ?? 0);
    setEditStartDate(task?.startDate ?? '');
    setEditEndDate(task?.endDate ?? '');
    setEditLabelIds(task?.labelIds ?? []);
    setEditAssigneeIds(task?.assigneeIds ?? []);
    setEditSprintId(task?.sprintId ?? '');
    setSaveError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setSaveError('');
  }

  function toggleId<T extends string>(arr: T[], id: T): T[] {
    return arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const isCreate = props.mode === 'create';
  const viewTask = task;
  const viewLabels = labels.filter(l => viewTask?.labelIds.includes(l.labelId));
  const viewAssignees = members.filter(m => viewTask?.assigneeIds.includes(m.userId));
  const borderColor = viewLabels[0]?.color ?? 'var(--color-border)';
  const needsFooter = isCreate || editing;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div ref={panelRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>

        {/* Title */}
        {isCreate ? (
          <input
            value={createTitle}
            onChange={e => setCreateTitle(e.target.value)}
            placeholder="Task title"
            autoFocus
            style={{ fontSize: 16, fontWeight: 600, border: 'none', borderBottom: '2px solid var(--color-primary)', outline: 'none', padding: '4px 0', marginBottom: 12, background: 'transparent', width: '100%' }}
          />
        ) : editing ? (
          <input
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            autoFocus
            style={{ fontSize: 16, fontWeight: 600, border: 'none', borderBottom: '2px solid var(--color-primary)', outline: 'none', padding: '4px 0', marginBottom: 12, background: 'transparent', width: '100%' }}
          />
        ) : (
          <div
            onDoubleClick={startEdit}
            title="Double-click to edit"
            style={{ fontSize: 16, fontWeight: 600, padding: '4px 0', marginBottom: 12, borderBottom: `2px solid ${borderColor}`, cursor: 'text', lineHeight: 1.4 }}
          >
            {viewTask?.title}
          </div>
        )}

        {/* Status */}
        <Row label="Status" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
          {isCreate || editing ? (
            <select
              value={isCreate ? createStatus : editStatus}
              onChange={e => isCreate ? setCreateStatus(e.target.value) : setEditStatus(e.target.value)}
              style={{ fontSize: 12, width: '100%' }}
            >
              {columns.map(col => <option key={col.columnId} value={col.columnId}>{col.name}</option>)}
            </select>
          ) : (
            <span style={{ fontSize: 12 }}>
              {columns.find(c => c.columnId === viewTask?.status)?.name ?? viewTask?.status ?? '—'}
            </span>
          )}
        </Row>

        {/* Epic / Project */}
        <Row label="Epic / Project" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
          {isCreate || editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <select
                value={isCreate ? createEpicId : editEpicId}
                onChange={e => {
                  const id = e.target.value;
                  if (isCreate) { setCreateEpicId(id); setCreateProjectId(''); loadProjects(id); }
                  else { setEditEpicId(id); setEditProjectId(''); loadProjects(id); }
                }}
                style={{ fontSize: 12 }}
              >
                <option value="">Select epic…</option>
                {epics.map(ep => <option key={ep.epicId} value={ep.epicId}>{ep.name}</option>)}
              </select>
              <select
                value={isCreate ? createProjectId : editProjectId}
                onChange={e => isCreate ? setCreateProjectId(e.target.value) : setEditProjectId(e.target.value)}
                disabled={loadingProjects || !(isCreate ? createEpicId : editEpicId)}
                style={{ fontSize: 12 }}
              >
                <option value="">Select project…</option>
                {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.name}</option>)}
              </select>
            </div>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              {viewTask?.epicId
                ? epics.find(e => e.epicId === viewTask.epicId)?.name
                  ? `${epics.find(e => e.epicId === viewTask.epicId)!.name} / ${projects.find(p => p.projectId === viewTask.projectId)?.name ?? '…'}`
                  : <span className="muted">Double-click to change</span>
                : <span className="muted">None</span>}
            </span>
          )}
        </Row>

        {/* Sprint */}
        {sprints !== undefined && (
          <Row label="Sprint" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
            {isCreate ? (
              <span className="muted" style={{ fontSize: 12 }}>Assigned on create</span>
            ) : editing ? (
              <select
                value={editSprintId}
                onChange={e => setEditSprintId(e.target.value)}
                style={{ fontSize: 12, width: '100%' }}
              >
                <option value="">No sprint</option>
                {sprints.map(s => <option key={s.sprintId} value={s.sprintId}>{s.name}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {sprints.find(s => s.sprintId === viewTask?.sprintId)?.name ?? 'None'}
              </span>
            )}
          </Row>
        )}

        {/* Assignees */}
        <Row label="Assignees" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
          {isCreate || editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {members.map(m => (
                <label key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isCreate ? createAssigneeIds.includes(m.userId) : editAssigneeIds.includes(m.userId)}
                    onChange={() => isCreate
                      ? setCreateAssigneeIds(prev => toggleId(prev, m.userId))
                      : setEditAssigneeIds(prev => toggleId(prev, m.userId))}
                  />
                  {m.displayName}
                </label>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 12 }}>
              {viewAssignees.length > 0
                ? viewAssignees.map(m => m.displayName).join(', ')
                : <span className="muted">None</span>}
            </span>
          )}
        </Row>

        {/* Labels */}
        <Row label="Labels" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
          {isCreate || editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {labels.map(l => (
                <label key={l.labelId} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isCreate ? createLabelIds.includes(l.labelId) : editLabelIds.includes(l.labelId)}
                    onChange={() => isCreate
                      ? setCreateLabelIds(prev => toggleId(prev, l.labelId))
                      : setEditLabelIds(prev => toggleId(prev, l.labelId))}
                  />
                  <span style={{ width: 10, height: 10, background: l.color, borderRadius: 2, display: 'inline-block' }} />
                  {l.name}
                </label>
              ))}
            </div>
          ) : (
            viewLabels.length > 0
              ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {viewLabels.map(l => (
                    <span key={l.labelId} style={{ fontSize: 11, background: l.color, color: '#fff', borderRadius: 10, padding: '1px 7px' }}>{l.name}</span>
                  ))}
                </div>
              : <span className="muted" style={{ fontSize: 12 }}>None</span>
          )}
        </Row>

        {/* Points / Complexity */}
        <Row label="Points / Cmplx" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
          {isCreate || editing ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number"
                min={0}
                value={isCreate ? createPoints : editPoints}
                onChange={e => isCreate
                  ? setCreatePoints(Math.max(0, Number(e.target.value)))
                  : setEditPoints(Math.max(0, Number(e.target.value)))}
                style={{ width: 60, fontSize: 12 }}
              />
              <select
                value={isCreate ? createComplexity : editComplexity}
                onChange={e => isCreate
                  ? setCreateComplexity(Number(e.target.value))
                  : setEditComplexity(Number(e.target.value))}
                style={{ fontSize: 12 }}
              >
                {[0, 1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{COMPLEXITY_LABELS[v]}</option>)}
              </select>
            </div>
          ) : (
            <span style={{ fontSize: 12 }}>
              {(viewTask?.points ?? 0) > 0 && <span className="badge" style={{ marginRight: 4 }}>{viewTask!.points} pt</span>}
              {(viewTask?.complexity ?? 0) > 0 && <span className="badge" style={{ background: '#7c3aed', color: '#fff' }}>C{viewTask!.complexity}</span>}
              {!(viewTask?.points) && !(viewTask?.complexity) && <span className="muted">—</span>}
            </span>
          )}
        </Row>

        {/* Dates */}
        <Row label="Dates" onDoubleClick={!isCreate && !editing ? startEdit : undefined}>
          {isCreate || editing ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                Start
                <input type="date" value={isCreate ? createStartDate : editStartDate}
                  onChange={e => isCreate ? setCreateStartDate(e.target.value) : setEditStartDate(e.target.value)}
                  style={{ fontSize: 12 }} />
              </label>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                End
                <input type="date" value={isCreate ? createEndDate : editEndDate}
                  onChange={e => isCreate ? setCreateEndDate(e.target.value) : setEditEndDate(e.target.value)}
                  style={{ fontSize: 12 }} />
              </label>
            </div>
          ) : (
            <span style={{ fontSize: 12 }}>
              {viewTask?.startDate || viewTask?.endDate
                ? (viewTask.startDate && viewTask.endDate
                    ? `${viewTask.startDate} → ${viewTask.endDate}`
                    : viewTask.startDate ? `Start: ${viewTask.startDate}` : `Due: ${viewTask.endDate}`)
                : <span className="muted">—</span>}
            </span>
          )}
        </Row>

        {/* Description */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>Description</div>
          {isCreate || editing ? (
            <RichTextEditor
              value={isCreate ? createDescription : editDescription}
              onChange={isCreate ? setCreateDescription : setEditDescription}
              minHeight={120}
            />
          ) : (
            <div
              onDoubleClick={startEdit}
              title="Double-click to edit"
              style={{ cursor: 'text', minHeight: 48, padding: '4px 0' }}
            >
              {viewTask?.description
                ? <RichTextContent html={viewTask.description} />
                : <span className="muted" style={{ fontSize: 12 }}>Double-click to add a description…</span>}
            </div>
          )}
        </div>

        {saveError && <p className="error" style={{ margin: '8px 0 0', fontSize: 12 }}>{saveError}</p>}
        {createError && <p className="error" style={{ margin: '8px 0 0', fontSize: 12 }}>{createError}</p>}
      </div>

      {/* Sticky footer — only in create mode or edit mode */}
      {needsFooter && (
        <div
          style={{
            borderTop: '1px solid var(--color-border)',
            padding: '12px 16px',
            flexShrink: 0,
            background: 'var(--color-surface)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          {isCreate ? (
            <>
              <button
                type="submit"
                disabled={creating || !createTitle.trim() || !createProjectId}
                onClick={doCreate}
              >
                {creating ? 'Creating…' : 'Create Task'}
              </button>
              {props.mode === 'create' && props.onCancel && (
                <button type="button" onClick={props.onCancel} style={{ background: 'none' }}>
                  Cancel
                </button>
              )}
            </>
          ) : (
            <>
              <button type="submit" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={cancelEdit} style={{ background: 'none' }}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default TaskPanel;
